import React from 'react';
import moment from 'moment';
import { Avatar } from 'components/elements';
import { chatMessageDateTimeFormat } from 'settings/configs';
import type { Message } from './types';
import {
  ChatGroupIncomingRow,
  ChatMessageBubble,
  ChatMessageContent,
  ChatMessageMeta,
  ChatMessageRow,
  ChatMessageSender,
  ChatMessageStatus,
  ChatMessageTime,
} from './chat.styled';
import { colors } from 'settings/style';

type MessageBubbleProps = {
  message: Message;
  isMine: boolean;
  isGroup: boolean;
};

const DoubleCheckIcon: React.FC<{
  size?: number;
  color?: string;
  double_check?: boolean;
}> = ({ size = 18, color = '#74A4D3', double_check = true }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5 12.5l3 4L16.5 8"
      stroke={color}
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {double_check && (
      <path
        d="M12 12.5l3 4L23.5 8"
        stroke={color}
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </svg>
);

const StatusIcon: React.FC<{ read?: boolean }> = ({ read }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
    <DoubleCheckIcon color={colors.primary500} size={18} double_check={read} />
  </span>
);

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isMine, isGroup }) => {
  const showGroupIncoming = isGroup && !isMine;
  const sentAt = moment(message.created_at).format(chatMessageDateTimeFormat);

  return (
    <ChatMessageRow $mine={isMine}>
      {showGroupIncoming ? (
        <ChatGroupIncomingRow>
          {message.senderAvatar && (
            <Avatar avatarUrl={message.senderAvatar} size="xsm" />
          )}
          <ChatMessageBubble $mine={false}>
            {message.senderName && (
              <ChatMessageSender>{message.senderName}</ChatMessageSender>
            )}
            <ChatMessageContent>{message.content}</ChatMessageContent>
            <ChatMessageMeta>
              <ChatMessageTime>{sentAt}</ChatMessageTime>
            </ChatMessageMeta>
          </ChatMessageBubble>
        </ChatGroupIncomingRow>
      ) : (
        <ChatMessageBubble $mine={isMine}>
          <ChatMessageContent>{message.content}</ChatMessageContent>
          <ChatMessageMeta>
            <ChatMessageTime>{sentAt}</ChatMessageTime>
            {isMine && (
              <ChatMessageStatus $read={message.status === 'read'}>
                <StatusIcon read={message.status === 'read'} />
              </ChatMessageStatus>
            )}
          </ChatMessageMeta>
        </ChatMessageBubble>
      )}
    </ChatMessageRow>
  );
};

export default MessageBubble;
